/** @shell standard @header bar 
 * V3 consultation screen — parity with v1 (consultation UI refresh).
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  Alert,
  Linking,
  Platform,
  Dimensions,
  AppState,
  AppStateStatus,
  LayoutAnimation,
  UIManager,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import Animated from 'react-native-reanimated';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll, HeaderIconButton } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { Input } from '../components/common/Input';
import { SolidCtaClickable } from '../components/common/SolidCtaClickable';
import { SegmentedControl, SEGMENTED_HEADER_BLOCK_HEIGHT } from '../components/common/SegmentedControl';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { androidScaleFont, androidScaleSize } from '../shared/theme/sharedStyles';
import { createCompactHeroCtaLabelStyle, createCompactHeroCtaSurfaceStyle } from '../shared/theme/styles/buttons';
import { useRtl } from '../shared/i18n/useRtl';
import { createSheetFooterDividerStyle } from '../shared/theme/styles/sheets';
import { resolveImageUri } from '../shared/utils/resolveImageUri';
import { formatCurrency } from '../shared/utils/currencyUtils';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  studioSectionLabelStyle,
} from '../shared/theme/studio/stackScreenShell';
import { useConsultants, useMyConsultations, useBookConsultation } from '../data/domains/consultations';
import type { ConsultantApi, MyConsultationBooking } from '../data/domains/consultations';
import { LoadingState } from '../components/common/LoadingState';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { ErrorState } from '../components/common/ErrorState';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { HealthInformationPopupScrollBody } from '../healthInformation/components/HealthInformationScrollBody';
import { useHealthInformationCms } from '../healthInformation/components/HealthInformationCmsContext';
import AppPickerTrigger from '../components/common/AppPickerTrigger';
import { useAuthStore } from '../shared/auth/store';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireConsultationsEnabled } from '../shared/navigation/useFeatureGateExit';
import { useOrganizationSettings } from '../data/domains/config';
import { useTranslation, getApiText, layoutLtrStyle } from '../shared/i18n';
import { useAuthSheetKeyboardInset } from '../shared/auth';
import moment from 'moment';
import {
  buildConsultationSlotOptions,
  countBookableConsultationSlots,
  resolveV3ConsultationBookingDateRange,
} from '../data/domains/consultations';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';

/** Height of tab strip + `ScreenHeader` `bottomContent` padding — keep in sync with `SegmentedControl`. */
const CONSULTATION_TAB_BAR_BLOCK_HEIGHT = SEGMENTED_HEADER_BLOCK_HEIGHT;

const { width: WINDOW_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const SCREEN_WIDTH = resolveWebLayoutWidth(WINDOW_WIDTH);
const MODAL_HEIGHT_RATIO = 0.88;
const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const CONSULTANT_AVATAR_LIST = 72;
const CONSULTANT_AVATAR_BOOKING = 64;
const CONSULTANT_AVATAR_MODAL = 56;
/** Shared min height for price chip + book CTA on consultant list cards. */
const CONSULTANT_FOOTER_ACTION_MIN_HEIGHT = androidScaleSize(48);
const BOOKING_SLOT_GRID_GAP = appTheme.spacing.sm;
const BOOKING_SLOT_GRID_AVAILABLE_WIDTH = SCREEN_WIDTH - appTheme.spacing.lg * 2;
const BOOKING_SLOT_WIDTH = Math.min(
  androidScaleSize(168),
  Math.max(androidScaleSize(120), Math.floor((BOOKING_SLOT_GRID_AVAILABLE_WIDTH - BOOKING_SLOT_GRID_GAP) / 2)),
);

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

type BookingStep = 'date' | 'time';

/** Eased cross-fade/height animation when moving between booking wizard steps. */
function animateBookingStepTransition() {
  LayoutAnimation.configureNext(LayoutAnimation.create(240, 'easeInEaseOut', 'opacity'));
}

/** Local noon avoids DST/off-by-one when converting calendar picks to `YYYY-MM-DD`. */
function ymdToLocalNoon(ymd: string): Date {
  const m = moment(ymd, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return moment().startOf('day').toDate();
  }
  return new Date(m.year(), m.month(), m.date(), 12, 0, 0, 0);
}

function dateToYmd(d: Date): string {
  if (!d || Number.isNaN(d.getTime())) {
    return moment().format('YYYY-MM-DD');
  }
  return moment(d).format('YYYY-MM-DD');
}

function clampYmdBetween(ymd: string, minYmd: string, maxYmd: string): string {
  const m = moment(ymd, 'YYYY-MM-DD', true);
  const minM = moment(minYmd, 'YYYY-MM-DD', true);
  const maxM = moment(maxYmd, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return minYmd;
  }
  if (m.isBefore(minM)) {
    return minYmd;
  }
  if (m.isAfter(maxM)) {
    return maxYmd;
  }
  return m.format('YYYY-MM-DD');
}

/** API may return a string or a per-day map (sunday–saturday). React cannot render plain objects as text children. */
function formatWorkingHoursForDisplay(working_hours: unknown): string {
  if (working_hours == null) {
    return '';
  }
  if (typeof working_hours === 'string') {
    return working_hours;
  }
  if (typeof working_hours === 'object' && !Array.isArray(working_hours)) {
    const day_order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const o = working_hours as Record<string, unknown>;
    const parts: string[] = [];
    for (const day of day_order) {
      const raw = o[day];
      if (raw != null && typeof raw !== 'object') {
        const s = String(raw).trim();
        if (s !== '') {
          parts.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: ${s}`);
        }
      }
    }
    if (parts.length > 0) {
      return parts.join('\n');
    }
    for (const [k, v] of Object.entries(o)) {
      if (v != null && typeof v !== 'object') {
        const s = String(v).trim();
        if (s !== '') {
          parts.push(`${k}: ${s}`);
        }
      }
    }
    return parts.join('\n');
  }
  return String(working_hours);
}

function ConsultantAvatar({
  uri,
  size,
  colors,
  style,
}: {
  uri?: string | null;
  size: number;
  colors: ThemeColors;
  style?: ViewStyle;
}) {
  const resolved = useMemo(() => resolveImageUri(uri ?? undefined), [uri]);
  const [failed, set_failed] = useState(false);
  const dim = androidScaleSize(size);
  const radius = dim * 0.28;
  const icon_size = Math.max(22, Math.round(dim * 0.38));

  return (
    <View
      style={[
        {
          width: dim,
          height: dim,
          borderRadius: radius,
          overflow: 'hidden',
          backgroundColor: colors.surfaceLight ?? hexToRgba(colors.text, 0.06),
          flexShrink: 0,
        },
        style,
      ]}>
      {resolved && !failed ? (
        <Image
          source={{ uri: resolved }}
          style={{ width: '100%', height: '100%' }}
          resizeMode="cover"
          onError={() => set_failed(true)}
        />
      ) : (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.primaryUltra ?? hexToRgba(colors.primary, 0.12),
          }}>
          <SvgIcon name="user" size={icon_size} color={colors.primary} />
        </View>
      )}
    </View>
  );
}

function BookingSectionLabel({
  label,
  styles,
}: {
  label: string;
  styles: ReturnType<typeof createConsultationStyles>;
}) {
  const rtl = useRtl();
  return (
    <Text style={[styles.sectionLabel, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>{label}</Text>
  );
}

function BookingStepIndicator({
  activeStep,
  dateComplete,
  styles,
  colors,
  rtl,
  t,
}: {
  activeStep: BookingStep;
  dateComplete: boolean;
  styles: ReturnType<typeof createConsultationStyles>;
  colors: ThemeColors;
  rtl: ReturnType<typeof useRtl>;
  t: (key: string) => string;
}) {
  const steps: { key: BookingStep; label: string }[] = [
    { key: 'date', label: t('consultation.stepDate') },
    { key: 'time', label: t('consultation.stepTime') },
  ];
  return (
    <View style={[styles.stepIndicatorRow, layoutLtrStyle]}>
      {steps.map((step, index) => {
        const is_active = step.key === activeStep;
        const is_done = step.key === 'date' && activeStep === 'time' && dateComplete;
        return (
          <React.Fragment key={step.key}>
            {index > 0 ? (
              <View style={[styles.stepConnector, activeStep === 'time' && styles.stepConnectorActive]} />
            ) : null}
            <View style={[styles.stepPill, (is_active || is_done) && styles.stepPillActive]}>
              <View style={[styles.stepDot, (is_active || is_done) && styles.stepDotActive]}>
                {is_done ? (
                  <SvgIcon name="check" size={12} color={colors.onPrimary} />
                ) : (
                  <Text style={[styles.stepDotText, is_active && styles.stepDotTextActive]}>{index + 1}</Text>
                )}
              </View>
              <Text style={[styles.stepPillLabel, (is_active || is_done) && styles.stepPillLabelActive]} numberOfLines={1}>
                {step.label}
              </Text>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );
}

const getStatusGradient = (status: string, themeColors: ThemeColors): [string, string] => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return [`${themeColors.success}25`, `${themeColors.success}08`];
    case 'pending':
      return [`${themeColors.secondary}25`, `${themeColors.secondary}08`];
    case 'rejected':
      return [`${themeColors.error}25`, `${themeColors.error}08`];
    case 'rescheduled':
      return [`${themeColors.warning}25`, `${themeColors.warning}08`];
    case 'completed':
      return [`${themeColors.alert}25`, `${themeColors.alert}08`];
    default:
      return [`${themeColors.secondary}25`, `${themeColors.secondary}08`];
  }
};

interface ConsultantCardProps {
  consultant: ConsultantApi;
  onBook: () => void;
  colors: ThemeColors;
  styles: ReturnType<typeof createConsultationStyles>;
}

const ConsultantCard: React.FC<ConsultantCardProps> = ({ consultant, onBook, colors, styles }) => {
  const { t } = useTranslation();
  const rtl = useRtl();
  const hours_display = formatWorkingHoursForDisplay(consultant.working_hours);
  const has_offer =
    consultant.offer_price != null && consultant.offer_price > 0 && consultant.offer_price < consultant.price;
  const consultant_name = getApiText(consultant as unknown as Record<string, unknown>, 'name', 'a_name') || consultant.name;
  const consultant_desc =
    getApiText(consultant as unknown as Record<string, unknown>, 'description', 'a_description') || consultant.description;

  return (
    <View style={styles.mainCard}>
      <View style={styles.consultantCardBody}>
        <View style={styles.consultantHeaderRow}>
          <ConsultantAvatar uri={consultant.picture} size={CONSULTANT_AVATAR_LIST} colors={colors} />
          <View style={styles.consultantHeaderText}>
            <Text
              style={[styles.consultantName, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
              numberOfLines={2}>
              {consultant_name}
            </Text>
            {consultant_desc ? (
              <Text
                style={[styles.consultantAbout, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                numberOfLines={3}>
                {consultant_desc}
              </Text>
            ) : null}
          </View>
        </View>

        {hours_display ? (
          <View style={styles.consultantHoursBlock}>
            <Text style={[styles.conTitle, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
              {t('consultation.workingHour')}
            </Text>
            <Text style={[styles.conNotes, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
              {hours_display}
            </Text>
          </View>
        ) : null}

        <View style={styles.consultantFooterRow}>
          <View style={styles.priceBlock}>
            {has_offer ? (
              <>
                <Text style={styles.oldPrice}>
                  {formatCurrency(Number(consultant.price) || 0)}
                </Text>
                <Text style={styles.newPrice}>
                  {formatCurrency(Number(consultant.offer_price) || 0)}
                </Text>
              </>
            ) : (
              <Text style={styles.newPrice}>
                {formatCurrency(Number(consultant.price) || 0)}
              </Text>
            )}
          </View>
          <Clickable
            onPress={onBook}
            useNativePressable
            pressAnimated={false}
            style={styles.bookCtaInline}
            contentBorderRadius={appTheme.borderRadius.lg}
            contentStyle={styles.bookCtaInner}
            accessibilityLabel={t('consultation.book')}
            accessibilityRole="button"
            haptics>
            <Text style={styles.bookCtaLabel} numberOfLines={1}>
              {t('consultation.bookAppointment')}
            </Text>
          </Clickable>
        </View>
      </View>
    </View>
  );
};

interface BookingCardProps {
  booking: MyConsultationBooking;
  onPress: () => void;
  colors: ThemeColors;
  styles: ReturnType<typeof createConsultationStyles>;
}

const getStatusColor = (status: string, themeColors: ThemeColors) => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return themeColors.success;
    case 'pending':
      return themeColors.secondary;
    case 'rejected':
      return themeColors.error;
    case 'rescheduled':
      return themeColors.warning;
    case 'completed':
      return themeColors.alert;
    default:
      return themeColors.textSecondary;
  }
};

const BookingCard: React.FC<BookingCardProps> = ({ booking, onPress, colors, styles }) => {
  const rtl = useRtl();
  const status_raw = booking.status ?? 'pending';
  const statusColor = getStatusColor(status_raw, colors);
  const bgColor = getStatusGradient(status_raw, colors)[0];
  const consultant_name = booking.consultant
    ? getApiText(booking.consultant as unknown as Record<string, unknown>, 'name', 'a_name') || booking.consultant.name
    : '';
  const consultant_desc = booking.consultant
    ? getApiText(booking.consultant as unknown as Record<string, unknown>, 'description', 'a_description') ||
      booking.consultant.description ||
      formatWorkingHoursForDisplay(booking.consultant.working_hours)
    : '';
  const date_label =
    booking.date != null && booking.date !== ''
      ? moment(booking.date, [moment.ISO_8601, 'YYYY-MM-DD'], true).isValid()
        ? moment(booking.date, [moment.ISO_8601, 'YYYY-MM-DD'], true).format('ddd, D MMM YYYY')
        : String(booking.date)
      : '';

  return (
    <Clickable style={styles.mainCard} onPress={onPress} pressAnimated={false} haptics>
      <View style={[styles.bookingCardGradient, { backgroundColor: bgColor }]}>
        <View style={styles.bookingCardInner}>
          <ConsultantAvatar uri={booking.consultant?.picture} size={CONSULTANT_AVATAR_BOOKING} colors={colors} />
          <View style={styles.bookingCardContent}>
            <View style={[styles.conStatusBadge, { backgroundColor: `${statusColor}30` }]}>
              <Text style={[styles.conStatusText, { color: statusColor }]}>{String(status_raw).toUpperCase()}</Text>
            </View>
            <Text
              style={[styles.consultantName, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
              numberOfLines={1}>
              {consultant_name}
            </Text>
            {consultant_desc ? (
              <Text
                style={[styles.consultantAbout, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                numberOfLines={2}>
                {consultant_desc}
              </Text>
            ) : null}
            {booking.notes ? (
              <Text
                style={[styles.bookingNotes, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                numberOfLines={2}>
                {booking.notes}
              </Text>
            ) : null}
            {date_label && booking.time_slot ? (
              <View style={styles.bookingScheduleRow}>
                <SvgIcon name="calendar" size={14} color={colors.textSecondary} />
                <Text style={styles.bookingDateText} numberOfLines={1}>
                  {date_label}
                </Text>
                <Text style={styles.bookingScheduleDot}>·</Text>
                <SvgIcon name="time" size={14} color={colors.textSecondary} />
                <Text style={styles.conSlot} numberOfLines={1}>
                  {booking.time_slot}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Clickable>
  );
};

export const ConsultationScreen: React.FC = () => {
  const { t, locale } = useTranslation();
  const on_back_exit = useExitOrGoBack();
  const consultations_enabled = useRequireConsultationsEnabled();
  const { openLogin, openRegister } = useAuthSheets();
  const insets = useSafeAreaInsets();
  const keyboard_inset = useAuthSheetKeyboardInset();
  const user = useAuthStore(s => s.user);
  const [activeTab, setActiveTab] = useState<'book' | 'history'>('book');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [health_info_sheet_open, setHealthInfoSheetOpen] = useState(false);
  const { isAvailable: health_info_available, pageTitle: health_info_page_title } = useHealthInformationCms();
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantApi | null>(null);
  const [selectedDate, _setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [bookingDate, setBookingDate] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [androidDatePickerOpen, setAndroidDatePickerOpen] = useState(false);
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [bookingStep, setBookingStep] = useState<BookingStep>('date');
  const appearance = useThemeStore(s => s.appearance);
  const datePickerTheme = appearance === 'dark' ? 'dark' : 'light';

  const appState = useRef<AppStateStatus>(AppState.currentState);
  const urlListenerRef = useRef<any>(null);

  const { data: consultants = [], isLoading: loadingConsultants, error: consultantsError, refetch: refetchConsultants } = useConsultants(user?.id || 0, selectedDate);
  const { data: consultantsForBookingDate = [], isLoading: loadingSlotsForDate } = useConsultants(
    user?.id || 0,
    showBookingModal && bookingDate ? bookingDate : '',
  );
  const { data: bookings = [], isLoading: loadingBookings, error: bookingsError, refetch: refetchBookings } = useMyConsultations(user?.id ?? 0);

  const consultant_list = useMemo(() => consultants ?? [], [consultants]);
  const consultant_with_slots = useMemo(() => {
    if (!selectedConsultant || !showBookingModal) {return selectedConsultant;}
    const from_date_list = consultantsForBookingDate.find(c => c.id === selectedConsultant.id);
    return from_date_list ?? selectedConsultant;
  }, [selectedConsultant, consultantsForBookingDate, showBookingModal]);
  const filteredConsultants = useMemo(() => {
    const active = consultant_list
      .filter(c => c.is_active)
      .map(c => {
        const consultant: ConsultantApi = { ...c };
        if (consultant.name === 'Yasmeen') {
          consultant.name = 'Yasmeen (Regency Salmiya)';
        } else if (consultant.name === 'Ramy') {
          consultant.name = 'Ramy (Jahra)';
        }
        return consultant;
      });
    if (!searchQuery.trim()) {
      return active;
    }
    return active.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || (c as { a_name?: string }).a_name?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [consultant_list, searchQuery]);

  const bookingsNewestFirst = useMemo(() => {
    return [...bookings].sort((a, b) => {
      const date_a = a.date || '';
      const date_b = b.date || '';
      if (date_b !== date_a) {return date_b.localeCompare(date_a);}
      const created_a = a.created_at ? new Date(a.created_at).getTime() : 0;
      const created_b = b.created_at ? new Date(b.created_at).getTime() : 0;
      return created_b - created_a;
    });
  }, [bookings]);

  const { settings: org_settings } = useOrganizationSettings();
  const day_after = Number(org_settings?.day_after ?? org_settings?.package_start_after ?? 3);
  const bookMutation = useBookConsultation();

  const refetch = () => {
    refetchConsultants();
    refetchBookings();
  };

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createConsultationStyles(colors), [colors]);
  const rtl = useRtl();
  const content_top = useHeaderTopPadding(false, CONSULTATION_TAB_BAR_BLOCK_HEIGHT);
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const booking_slot_options = useMemo(() => {
    if (!bookingDate || !consultant_with_slots) {
      return [];
    }
    return buildConsultationSlotOptions(
      consultant_with_slots.time_slots,
      bookingDate,
      bookings,
      consultant_with_slots.booked_consultations,
    );
  }, [bookingDate, consultant_with_slots, bookings]);

  const bookable_slot_count = useMemo(() => countBookableConsultationSlots(booking_slot_options), [booking_slot_options]);

  useEffect(() => {
    if (!selectedTimeSlot) {
      return;
    }
    const still_bookable = booking_slot_options.some(o => o.time_slot === selectedTimeSlot && o.bookable);
    if (!still_bookable) {
      setSelectedTimeSlot('');
    }
  }, [booking_slot_options, selectedTimeSlot]);

  const is_dark = appearance === 'dark';
  const health_info_glass_surface = useMemo(
    () => ({
      iconColor: hexToRgba(colors.danger, is_dark ? 0.93 : 0.86),
    }),
    [colors.danger, is_dark],
  );

  const health_info_header_right = useMemo(
    () => (
      <HeaderIconButton
        onPress={() => setHealthInfoSheetOpen(true)}
        accessibilityLabel={t('healthInformation.openInfoAccessibility')}
        appearance="outline"
        haptics>
        <SvgIcon name="health-alert" size={22} color={health_info_glass_surface.iconColor} />
      </HeaderIconButton>
    ),
    [health_info_glass_surface.iconColor, t],
  );

  const consultation_tab_options = useMemo(
    () => [
      { value: 'book' as const, label: t('consultation.bookAppointment') },
      { value: 'history' as const, label: t('consultation.myBookings') },
    ],
    [t],
  );

  const consultation_header_tabs = useMemo(
    () => (
      <View style={styles.tabBarOuter}>
        <SegmentedControl
          colors={colors}
          options={consultation_tab_options}
          value={activeTab}
          onChange={setActiveTab}
          style={styles.tabBar}
          testID="consultation-header-tabs"
        />
      </View>
    ),
    [activeTab, colors, consultation_tab_options, styles.tabBar, styles.tabBarOuter],
  );

  const bookingDateRange = useMemo(() => {
    if (!minDate || !maxDate) {
      return { minYmd: minDate, maxYmd: maxDate };
    }
    const a = moment(minDate, 'YYYY-MM-DD', true);
    const b = moment(maxDate, 'YYYY-MM-DD', true);
    if (!a.isValid() || !b.isValid()) {
      return { minYmd: minDate, maxYmd: maxDate };
    }
    return a.isAfter(b) ? { minYmd: maxDate, maxYmd: minDate } : { minYmd: minDate, maxYmd: maxDate };
  }, [minDate, maxDate]);

  const booking_date_display = useMemo(() => {
    if (!bookingDate) {
      return '';
    }
    return moment(bookingDate).locale(locale === 'ar' ? 'ar' : 'en').format('ddd, D MMM YYYY');
  }, [bookingDate, locale]);

  const resetBookingForm = useCallback(() => {
    setSelectedConsultant(null);
    setBookingDate('');
    setSelectedTimeSlot('');
    setBookingNotes('');
    setMinDate('');
    setMaxDate('');
    setAndroidDatePickerOpen(false);
    setBookingStep('date');
  }, []);

  const closeBookingModal = useCallback(() => {
    setShowBookingModal(false);
    resetBookingForm();
  }, [resetBookingForm]);

  useEffect(() => {
    const cleanup = setupAppStateListener();
    return () => {
      if (urlListenerRef.current) {
        urlListenerRef.current.remove();
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  const setupAppStateListener = () => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      appState.current = nextAppState;
    });
    return () => subscription?.remove();
  };

  const handleDeepLink = (event: { url: string }) => {
    const url = event.url;

    if (url.indexOf('?status=1') > -1 || url.indexOf('&status=1') > -1) {
      Alert.alert(t('common.success'), t('consultation.paymentSuccess'));
      refetch();
      setActiveTab('history');
    } else if (url.indexOf('?status=0') > -1 || url.indexOf('&status=0') > -1) {
      Alert.alert(t('common.error'), t('consultation.paymentFailed'));
    }

    if (urlListenerRef.current) {
      urlListenerRef.current.remove();
      urlListenerRef.current = null;
    }
  };

  const setupUrlListener = () => {
    if (urlListenerRef.current) {
      urlListenerRef.current.remove();
    }

    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      urlListenerRef.current = Linking.addEventListener('url', handleDeepLink);
    }
  };

  const handleSearch = (query: string) => setSearchQuery(query);

  const handleSelectConsultant = (consultant: ConsultantApi) => {
    if (!user) {
      Alert.alert(t('consultation.accountRequiredTitle'), t('consultation.accountRequiredMessage'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('auth.signIn'), onPress: () => openLogin() },
        { text: t('auth.createAccount'), onPress: () => openRegister() },
      ]);
      return;
    }
    openBookingModal(consultant);
  };

  const openBookingModal = (consultant: ConsultantApi) => {
    setAndroidDatePickerOpen(false);
    setSelectedConsultant(consultant);
    setSelectedTimeSlot('');
    setBookingNotes('');

    const { min_ymd, max_ymd } = resolveV3ConsultationBookingDateRange(consultant, day_after);
    setMinDate(min_ymd);
    setMaxDate(max_ymd);
    setBookingDate(min_ymd);
    setBookingStep('date');
    setShowBookingModal(true);
  };

  const goToBookingStep = useCallback((step: BookingStep) => {
    animateBookingStepTransition();
    setAndroidDatePickerOpen(false);
    setBookingStep(step);
  }, []);

  const onBookingDateChange = useCallback((text: string) => {
    setBookingDate(text);
    setSelectedTimeSlot('');
  }, []);

  /** Inline picker lives outside ScrollView (same Modal as sheet) — avoids nested Modal + scroll gesture bugs. */
  const handleBookingDateNativeChange = useCallback(
    (_event: { type?: string }, date?: Date) => {
      if (Platform.OS === 'android') {
        setAndroidDatePickerOpen(false);
      }
      if (Platform.OS === 'android' && _event?.type === 'dismissed') {
        return;
      }
      if (!date || Number.isNaN(date.getTime())) {
        return;
      }
      const { minYmd, maxYmd } = bookingDateRange;
      if (!minYmd || !maxYmd) {
        return;
      }
      const ymd = dateToYmd(date);
      onBookingDateChange(clampYmdBetween(ymd, minYmd, maxYmd));
    },
    [bookingDateRange, onBookingDateChange],
  );

  const handleBookConsultation = async () => {
    const slot_ok = booking_slot_options.some(o => o.time_slot === selectedTimeSlot && o.bookable);
    if (!user?.id || !selectedConsultant || !bookingDate || !selectedTimeSlot || !slot_ok) {
      Alert.alert(t('common.error'), t('consultation.selectDateAndTime'));
      return;
    }

    bookMutation.mutate(
      {
        customer_id: user.id,
        consultant_id: selectedConsultant.id,
        date: bookingDate,
        time_slot: selectedTimeSlot,
        notes: bookingNotes,
      },
      {
        onSuccess: (response: any) => {
          closeBookingModal();
          if (response?.consultation?.charge_url) {
            setupUrlListener();
            Linking.openURL(response.consultation.charge_url).catch(_err => {
              Alert.alert(t('common.error'), t('consultation.failedToOpenPayment'));
            });
          } else {
            Alert.alert(t('common.success'), response?.message || t('consultation.bookedSuccess'));
            refetch();
            setActiveTab('history');
          }
        },
        onError: (err: any) => {
          Alert.alert(t('common.error'), err?.message || t('consultation.failedToBook'));
        },
      },
    );
  };

  if (!consultations_enabled) {
    return null;
  }

  if (loadingConsultants) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader
          title={t('consultation.title')}
          onBack={on_back_exit}
          scrollY={scrollY}
          rightAction={health_info_available ? health_info_header_right : undefined}
          bottomContent={consultation_header_tabs}
        />
        <View style={[styles.stateWrap, { paddingTop: content_top + appTheme.spacing.sm }]}>
          <LoadingState />
        </View>
      </StackScreenShell>
    );
  }
  if (consultantsError) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader
          title={t('consultation.title')}
          onBack={on_back_exit}
          scrollY={scrollY}
          rightAction={health_info_available ? health_info_header_right : undefined}
          bottomContent={consultation_header_tabs}
        />
        <View style={[styles.stateWrap, { paddingTop: content_top + appTheme.spacing.sm }]}>
          <ErrorState
            title={t('common.error')}
            message={t('consultation.failedToLoad')}
            onRetry={refetchConsultants}
            error={consultantsError}
            issue_context={{
              page: t('consultation.title'),
              issue: 'Failed to load consultants',
              kind: 'api',
              api_endpoint: 'consultants',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader
        title={t('consultation.title')}
        onBack={on_back_exit}
        scrollY={scrollY}
        rightAction={health_info_available ? health_info_header_right : undefined}
        bottomContent={consultation_header_tabs}
      />

      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: content_top + appTheme.spacing.sm,
            paddingBottom: insets.bottom + STACK_SECTION_GAP,
          },
        ]}
        showsVerticalScrollIndicator={false}>
        {activeTab === 'book' ? (
          <>
            <View style={styles.searchContainer}>
              <Input
                placeholder={t('consultation.searchPlaceholder')}
                value={searchQuery}
                onChangeText={handleSearch}
                leftAccessory={<SvgIcon name="search" size={20} color={colors.textSecondary} />}
                rightAccessory={
                  searchQuery.length > 0 ? (
                    <Clickable onPress={() => handleSearch('')}>
                      <SvgIcon name="x" size={20} color={colors.textSecondary} />
                    </Clickable>
                  ) : null
                }
                containerStyle={styles.searchInputWrap}
              />
            </View>
            <Text style={styles.sectionTitle}>{t('consultation.availableConsultants')}</Text>
            {filteredConsultants.length > 0 ? (
              filteredConsultants.map(consultant => (
                <ConsultantCard
                  key={consultant.id}
                  consultant={consultant}
                  onBook={() => handleSelectConsultant(consultant)}
                  colors={colors}
                  styles={styles}
                />
              ))
            ) : (
              <View style={styles.emptyState}>
                <SvgIcon name="user" size={48} color={colors.textLight} />
                <Text style={styles.emptyText}>{t('consultation.noConsultantsAvailable')}</Text>
              </View>
            )}
          </>
        ) : (
          <>
            <Text style={styles.sectionTitle}>{t('consultation.scheduledConsultations')}</Text>
            {!user ? (
              <View style={styles.emptyState}>
                <SvgIcon name="user" size={48} color={colors.textLight} />
                <Text style={styles.emptyText}>{t('consultation.signInToSeeBookings')}</Text>
                <Clickable
                  style={styles.guestAuthCta}
                  onPress={() => openLogin()}
                  accessibilityRole="button"
                  accessibilityLabel={t('auth.signIn')}>
                  <Text style={styles.guestAuthCtaText}>{t('auth.signIn')}</Text>
                </Clickable>
                <Clickable
                  style={styles.guestAuthCtaSecondary}
                  onPress={() => openRegister()}
                  accessibilityRole="button"
                  accessibilityLabel={t('auth.createAccount')}>
                  <Text style={styles.guestAuthCtaTextSecondary}>{t('auth.createAccount')}</Text>
                </Clickable>
              </View>
            ) : bookingsError ? (
              <View style={styles.bookingsErrorWrap}>
                <ErrorState
                  title={t('common.error')}
                  message={t('consultation.failedToLoadBookings')}
                  onRetry={refetchBookings}
                  error={bookingsError}
                  issue_context={{
                    page: t('consultation.title'),
                    issue: 'Failed to load consultation bookings',
                    kind: 'api',
                    api_endpoint: 'consultations',
                    http_method: 'GET',
                  }}
                />
              </View>
            ) : loadingBookings && bookingsNewestFirst.length === 0 ? (
              <View style={styles.historyLoading}>
                <LoadingState />
              </View>
            ) : bookingsNewestFirst.length > 0 ? (
              bookingsNewestFirst.map((booking, index) => (
                <BookingCard
                  key={`${booking.date ?? 'd'}-${booking.time_slot ?? 't'}-${booking.consultant?.id ?? index}`}
                  booking={booking}
                  onPress={() => {}}
                  colors={colors}
                  styles={styles}
                />
              ))
            ) : (
              <View style={styles.emptyState}>
                <SvgIcon name="calendar" size={48} color={colors.textLight} />
                <Text style={styles.emptyText}>{t('consultation.noAppointmentsYet')}</Text>
                <Text style={styles.emptySubtext}>{t('consultation.noAppointmentsSubtext')}</Text>
              </View>
            )}
          </>
        )}
      </Animated.ScrollView>

      <BottomSheetPopup
        visible={showBookingModal}
        onClose={closeBookingModal}
        title={t('consultation.bookConsultationTitle')}
        animationType="slide"
        showDragHandle
        frostedSheetHeader
        sheetTopBorderRadius={DASH_STUDIO_RADIUS}
        keyboardAvoiding
        padBottomSafeArea={false}
        sheetStyle={styles.bookingSheet}>
            <View style={styles.modalInner}>
              {selectedConsultant && (
                <>
                  <View style={styles.modalSheetTop}>
                    <View style={styles.modalConsultantRow}>
                      <ConsultantAvatar uri={selectedConsultant.picture} size={CONSULTANT_AVATAR_MODAL} colors={colors} />
                      <View style={styles.modalConsultantTextCol}>
                        <Text
                          style={[styles.modalConsultantName, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                          numberOfLines={1}>
                          {getApiText(selectedConsultant as unknown as Record<string, unknown>, 'name', 'a_name') || selectedConsultant.name}
                        </Text>
                        {formatWorkingHoursForDisplay(selectedConsultant.working_hours) ? (
                          <Text
                            style={[styles.modalConsultantMeta, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                            numberOfLines={1}>
                            {formatWorkingHoursForDisplay(selectedConsultant.working_hours)}
                          </Text>
                        ) : null}
                      </View>
                    </View>

                    <BookingStepIndicator
                      activeStep={bookingStep}
                      dateComplete={!!bookingDate}
                      styles={styles}
                      colors={colors}
                      rtl={rtl}
                      t={t}
                    />
                  </View>

                  <ScrollView
                    style={styles.modalScroll}
                    contentContainerStyle={[
                      styles.modalScrollContent,
                      { paddingBottom: appTheme.spacing.xl + (Platform.OS === 'ios' ? 0 : keyboard_inset) },
                    ]}
                    keyboardShouldPersistTaps="always"
                    keyboardDismissMode="on-drag"
                    automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
                    showsVerticalScrollIndicator={false}
                    nestedScrollEnabled>
                    {bookingStep === 'date' ? (
                      <View style={styles.stepBody}>
                        <Text style={[styles.stepTitle, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
                          {t('consultation.pickDateTitle')}
                        </Text>
                        <Text style={[styles.dateHint, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
                          {t('consultation.dateRangeHint')
                            .replace('{{min}}', bookingDateRange.minYmd ?? '')
                            .replace('{{max}}', bookingDateRange.maxYmd ?? '')}
                        </Text>
                        {bookingDateRange.minYmd && bookingDateRange.maxYmd && bookingDate ? (
                          Platform.OS === 'android' ? (
                            <>
                              <AppPickerTrigger
                                label={t('consultation.dateLabel')}
                                value={booking_date_display || null}
                                placeholder={t('consultation.chooseAppointmentDate')}
                                onPress={() => setAndroidDatePickerOpen(true)}
                                accessibilityLabel={t('consultation.dateLabel')}
                                containerStyle={styles.datePickerTriggerWrap}
                              />
                              {androidDatePickerOpen ? (
                                <View style={styles.androidPickerWrap}>
                                  <DateTimePicker
                                    value={ymdToLocalNoon(bookingDate)}
                                    mode="date"
                                    display="default"
                                    onChange={handleBookingDateNativeChange}
                                    minimumDate={ymdToLocalNoon(bookingDateRange.minYmd)}
                                    maximumDate={ymdToLocalNoon(bookingDateRange.maxYmd)}
                                    themeVariant={datePickerTheme}
                                    locale={locale === 'ar' ? 'ar' : 'en'}
                                  />
                                </View>
                              ) : null}
                            </>
                          ) : (
                            <View style={styles.iosCalendarCentered}>
                              <View style={styles.iosInlineCalendarWrap}>
                                <DateTimePicker
                                  key={`consultation-inline-cal-${appearance}-${bookingDateRange.minYmd}-${bookingDateRange.maxYmd}`}
                                  value={ymdToLocalNoon(bookingDate)}
                                  mode="date"
                                  display="inline"
                                  onChange={handleBookingDateNativeChange}
                                  minimumDate={ymdToLocalNoon(bookingDateRange.minYmd)}
                                  maximumDate={ymdToLocalNoon(bookingDateRange.maxYmd)}
                                  themeVariant={datePickerTheme}
                                  locale={locale === 'ar' ? 'ar' : 'en'}
                                  style={styles.iosInlineCalendarPicker}
                                />
                              </View>
                            </View>
                          )
                        ) : null}
                      </View>
                    ) : (
                      <View style={styles.stepBody}>
                        <View style={[styles.selectedDatePill, layoutLtrStyle]}>
                          <SvgIcon name="calendar" size={16} color={colors.primary} />
                          <Text
                            style={[
                              styles.selectedDatePillText,
                              { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
                            ]}
                            numberOfLines={1}>
                            {booking_date_display}
                          </Text>
                          <Clickable
                            onPress={() => goToBookingStep('date')}
                            accessibilityRole="button"
                            accessibilityLabel={t('consultation.changeDate')}
                            hitSlop={appTheme.hitSlop.small}
                            haptics>
                            <Text style={styles.selectedDatePillAction}>{t('consultation.changeDate')}</Text>
                          </Clickable>
                        </View>

                        <Text style={[styles.stepTitle, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
                          {t('consultation.pickTimeTitle')}
                        </Text>

                        {loadingSlotsForDate ? (
                          <View style={styles.slotsLoadingWrap}>
                            <ButtonLoader size={22} color={colors.primary} />
                            <Text style={styles.slotsLoading}>{t('consultation.loadingSlots')}</Text>
                          </View>
                        ) : booking_slot_options.length > 0 ? (
                          <>
                            <View style={styles.slotGrid}>
                              {booking_slot_options.map((option, index) => {
                                const selected = selectedTimeSlot === option.time_slot;
                                const block_label =
                                  option.block_reason === 'customer'
                                    ? t('consultation.slotUnavailableYours')
                                    : option.block_reason === 'consultant'
                                      ? t('consultation.slotUnavailableConsultant')
                                      : null;
                                return (
                                  <Clickable
                                    key={`${option.time_slot}-${index}`}
                                    onPress={() => option.bookable && setSelectedTimeSlot(option.time_slot)}
                                    disabled={!option.bookable}
                                    style={styles.slotGridCell}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected, disabled: !option.bookable }}
                                    accessibilityLabel={option.time_slot}
                                    haptics>
                                    <View
                                      style={[
                                        styles.slotChip,
                                        selected && styles.slotChipActive,
                                        !option.bookable && styles.slotChipDisabled,
                                      ]}>
                                      <Text
                                        style={[
                                          styles.slotChipText,
                                          selected && styles.slotChipTextActive,
                                          !option.bookable && styles.slotChipTextDisabled,
                                        ]}
                                        numberOfLines={1}>
                                        {option.time_slot}
                                      </Text>
                                      {block_label ? (
                                        <Text style={styles.slotChipBlockedHint} numberOfLines={1}>
                                          {block_label}
                                        </Text>
                                      ) : null}
                                      {selected ? (
                                        <View style={styles.slotChipCheck}>
                                          <SvgIcon name="check" size={12} color={colors.onPrimary} />
                                        </View>
                                      ) : null}
                                    </View>
                                  </Clickable>
                                );
                              })}
                            </View>
                            {bookable_slot_count === 0 ? (
                              <Text style={[styles.slotsEmpty, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
                                {t('consultation.noSlotsForDate')}
                              </Text>
                            ) : null}
                          </>
                        ) : (
                          <Text style={[styles.slotsEmpty, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}>
                            {t('consultation.noSlotsForDate')}
                          </Text>
                        )}

                        <View style={styles.bookingSection}>
                          <BookingSectionLabel label={t('consultation.notesLabel')} styles={styles} />
                          <Input
                            placeholder={t('consultation.notesPlaceholder')}
                            value={bookingNotes}
                            onChangeText={setBookingNotes}
                            multiline
                            numberOfLines={3}
                            containerStyle={styles.notesInputWrap}
                          />
                        </View>
                      </View>
                    )}
                  </ScrollView>
                </>
              )}

              <View style={[styles.modalFooter, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.md) }]}>
                {bookingStep === 'date' ? (
                  <SolidCtaClickable
                    label={t('consultation.next')}
                    onPress={() => goToBookingStep('time')}
                    disabled={!bookingDate}
                    variant="primary"
                    haptics={true}
                    accessibilityLabel={t('consultation.next')}
                  />
                ) : (
                  <View style={styles.wizardFooterStack}>
                    <Clickable
                      onPress={() => goToBookingStep('date')}
                      style={styles.wizardBackBtn}
                      accessibilityRole="button"
                      accessibilityLabel={t('consultation.back')}
                      haptics>
                      <View style={styles.wizardBackContent}>
                        <SvgIcon name={rtl.leadingBackArrowIcon === 'arrow-right' ? 'chevron-right' : 'chevron-left'} size={18} color={colors.primary} />
                        <Text style={styles.wizardBackText}>{t('consultation.back')}</Text>
                      </View>
                    </Clickable>
                    <SolidCtaClickable
                      label={bookMutation.isPending ? t('consultation.bookingInProgress') : t('consultation.bookAppointment')}
                      onPress={handleBookConsultation}
                      disabled={!selectedTimeSlot || !bookingDate || bookMutation.isPending || bookable_slot_count === 0}
                      variant="primary"
                      haptics={true}
                      accessibilityLabel={t('consultation.bookAppointment')}
                    />
                  </View>
                )}
              </View>
            </View>
      </BottomSheetPopup>

      {health_info_available ? (
      <BottomSheetPopup
        visible={health_info_sheet_open}
        onClose={() => setHealthInfoSheetOpen(false)}
        title={health_info_page_title || t('healthInformation.title')}
        showDragHandle
        animationType="slide">
        <HealthInformationPopupScrollBody />
      </BottomSheetPopup>
      ) : null}
    </StackScreenShell>
  );
};

function createConsultationStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  const studio_card = studioCardShell(c);
  const slot_list_bg = (hexToRgba(c.text, 0.04) as string) || c.surfaceLight;
  const muted_label = hexToRgba(c.text, 0.58);
  const body_secondary = hexToRgba(c.text, 0.72);
  const book_cta_surface = createCompactHeroCtaSurfaceStyle(c, 'solidPrimary');
  const book_cta_label = createCompactHeroCtaLabelStyle(c, 'solidPrimary');
  return StyleSheet.create({
  stateWrap: {
    flex: 1,
  },
  tabBarOuter: {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: appTheme.spacing.sm,
    marginTop: appTheme.spacing.xs,
    marginBottom: appTheme.spacing.xs,
  },
  tabBar: {
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: STACK_SCROLL_GUTTER,
    gap: appTheme.spacing.md,
  },
  sectionTitle: {
    ...studioSectionLabelStyle(c),
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  mainCard: {
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
    ...studio_card,
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: hexToRgba(c.text, 0.08),
  },
  consultantCardBody: {
    width: '100%',
    padding: appTheme.spacing.lg,
    gap: appTheme.spacing.md,
  },
  consultantHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: appTheme.spacing.md,
    width: '100%',
  },
  consultantHeaderText: {
    flex: 1,
    minWidth: 0,
    paddingTop: 2,
  },
  consultantHoursBlock: {
    width: '100%',
    paddingTop: appTheme.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: hexToRgba(c.text, 0.1),
    gap: appTheme.spacing.xs,
  },
  consultantFooterRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: appTheme.spacing.md,
    width: '100%',
    paddingTop: appTheme.spacing.xs,
  },
  priceBlock: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 2,
    flexShrink: 0,
    alignSelf: 'stretch',
    minHeight: CONSULTANT_FOOTER_ACTION_MIN_HEIGHT,
    maxWidth: '38%',
    backgroundColor: hexToRgba(c.primary, 0.1),
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: hexToRgba(c.primary, 0.22),
    paddingHorizontal: appTheme.spacing.md,
    paddingVertical: appTheme.spacing.sm,
    borderRadius: appTheme.borderRadius.lg,
  },
  bookCtaInline: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    ...book_cta_surface,
    minHeight: CONSULTANT_FOOTER_ACTION_MIN_HEIGHT,
  },
  bookCtaInner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: appTheme.spacing.md,
  },
  bookCtaLabel: {
    ...book_cta_label,
    fontSize: sf(14),
    letterSpacing: 0.2,
    ...ANDROID_TEXT_COMPACT,
  },
  bookingCardGradient: {
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
  },
  bookingCardInner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: appTheme.spacing.md,
    padding: appTheme.spacing.md,
  },
  bookingCardContent: {
    flex: 1,
    minWidth: 0,
    gap: appTheme.spacing.xs,
  },
  bookingScheduleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: appTheme.spacing.xs,
  },
  bookingScheduleDot: {
    fontSize: sf(12),
    color: body_secondary,
    ...ANDROID_TEXT_COMPACT,
  },
  consultantAbout: {
    fontSize: sf(13),
    lineHeight: sf(19),
    color: body_secondary,
    marginTop: 4,
    ...ANDROID_TEXT_COMPACT,
  },
  conTitle: {
    fontSize: sf(11),
    fontWeight: '700',
    color: muted_label,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...ANDROID_TEXT_COMPACT,
  },
  conNotes: {
    fontSize: sf(14),
    fontWeight: '600',
    color: c.text,
    lineHeight: sf(20),
    ...ANDROID_TEXT_COMPACT,
  },
  conStatusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  conStatusText: {
    fontSize: sf(10),
    fontWeight: '700',
    letterSpacing: 0.8,
    ...ANDROID_TEXT_COMPACT,
  },
  bookingDateText: {
    fontSize: sf(12),
    color: c.text,
    fontWeight: '600',
    flexShrink: 1,
    ...ANDROID_TEXT_COMPACT,
  },
  conSlot: {
    fontSize: sf(12),
    fontWeight: '600',
    color: body_secondary,
    flexShrink: 1,
    ...ANDROID_TEXT_COMPACT,
  },
  bookingNotes: {
    fontSize: sf(12),
    fontWeight: '400',
    color: body_secondary,
    fontStyle: 'italic',
    ...ANDROID_TEXT_COMPACT,
  },
  consultantName: {
    fontSize: sf(16),
    letterSpacing: -0.2,
    fontWeight: '800',
    color: c.text,
    ...ANDROID_TEXT_COMPACT,
  },
  statusBadge: {
    paddingHorizontal: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.xs,
    borderRadius: appTheme.borderRadius.sm,
  },
  statusText: {
    ...appTheme.typography.caption,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  consultantDescription: {
    ...appTheme.typography.bodySmall,
    color: c.textSecondary,
    marginBottom: appTheme.spacing.xs,
  },
  workingHours: {
    ...appTheme.typography.caption,
    color: c.textSecondary,
    marginBottom: appTheme.spacing.xs,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.sm,
    marginBottom: appTheme.spacing.xs,
  },
  oldPrice: {
    ...appTheme.typography.bodySmall,
    color: c.error,
    textDecorationLine: 'line-through',
  },
  newPrice: {
    ...appTheme.typography.body,
    color: c.primary,
    fontWeight: '800',
    fontSize: sf(15),
    ...ANDROID_TEXT_COMPACT,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: appTheme.spacing.xs,
  },
  historyLoading: {
    minHeight: 200,
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.xl,
  },
  bookingsErrorWrap: {
    minHeight: 280,
  },
  searchInputWrap: {
    flex: 1,
    marginBottom: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.xl,
    paddingHorizontal: appTheme.spacing.md,
  },
  emptyText: {
    ...appTheme.typography.body,
    color: body_secondary,
    marginTop: appTheme.spacing.md,
    textAlign: 'center',
  },
  emptySubtext: {
    ...appTheme.typography.bodySmall,
    color: muted_label,
    textAlign: 'center',
    marginTop: appTheme.spacing.sm,
  },
  guestAuthCta: {
    marginTop: appTheme.spacing.lg,
    backgroundColor: c.primary,
    paddingVertical: 12,
    paddingHorizontal: appTheme.spacing.xl,
    borderRadius: appTheme.borderRadius.lg,
    alignSelf: 'stretch',
    maxWidth: 320,
    alignItems: 'center',
  },
  guestAuthCtaText: {
    ...appTheme.typography.body,
    color: c.onPrimary,
    fontWeight: '600',
  },
  guestAuthCtaSecondary: {
    marginTop: appTheme.spacing.md,
    paddingVertical: 12,
    paddingHorizontal: appTheme.spacing.xl,
    alignSelf: 'stretch',
    maxWidth: 320,
    alignItems: 'center',
  },
  guestAuthCtaTextSecondary: {
    ...appTheme.typography.body,
    color: c.primary,
    fontWeight: '600',
  },
  bookingSheet: {
    maxHeight: SCREEN_HEIGHT * MODAL_HEIGHT_RATIO,
    borderTopLeftRadius: DASH_STUDIO_RADIUS,
    borderTopRightRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
  },
  modalInner: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  modalScroll: {
    flex: 1,
    minHeight: 0,
  },
  modalSheetTop: {
    flexShrink: 0,
    paddingHorizontal: appTheme.spacing.lg,
    paddingTop: appTheme.spacing.md,
    paddingBottom: appTheme.spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: c.border,
  },
  modalConsultantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.md,
    width: '100%',
  },
  modalConsultantTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  modalScrollContent: {
    paddingHorizontal: appTheme.spacing.lg,
    paddingTop: appTheme.spacing.md,
    paddingBottom: appTheme.spacing.lg,
    gap: appTheme.spacing.md,
  },
  modalConsultantName: {
    fontSize: sf(17),
    fontWeight: '800',
    color: c.text,
    ...ANDROID_TEXT_COMPACT,
  },
  modalConsultantMeta: {
    fontSize: sf(12),
    lineHeight: sf(16),
    color: muted_label,
    ...ANDROID_TEXT_COMPACT,
  },
  bookingSection: {
    gap: appTheme.spacing.sm,
  },
  sectionLabel: {
    fontSize: sf(11),
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase' as const,
    color: muted_label,
    ...ANDROID_TEXT_COMPACT,
  },
  stepIndicatorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: appTheme.spacing.md,
    gap: appTheme.spacing.xs,
  },
  stepConnector: {
    width: androidScaleSize(28),
    height: 2,
    borderRadius: 1,
    backgroundColor: c.border,
  },
  stepConnectorActive: {
    backgroundColor: c.primary,
  },
  stepPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: slot_list_bg,
  },
  stepPillActive: {
    backgroundColor: hexToRgba(c.primary, 0.12) as string,
  },
  stepDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: c.border,
  },
  stepDotActive: {
    backgroundColor: c.primary,
  },
  stepDotText: {
    fontSize: sf(11),
    fontWeight: '800',
    color: muted_label,
    ...ANDROID_TEXT_COMPACT,
  },
  stepDotTextActive: {
    color: c.onPrimary,
  },
  stepPillLabel: {
    fontSize: sf(12),
    fontWeight: '700',
    color: muted_label,
    ...ANDROID_TEXT_COMPACT,
  },
  stepPillLabelActive: {
    color: c.primary,
  },
  stepBody: {
    gap: appTheme.spacing.md,
  },
  stepTitle: {
    fontSize: sf(18),
    fontWeight: '800',
    letterSpacing: -0.3,
    color: c.text,
    ...ANDROID_TEXT_COMPACT,
  },
  iosCalendarCentered: {
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
  },
  iosInlineCalendarPicker: {
    alignSelf: 'center',
  },
  selectedDatePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.md,
    borderRadius: appTheme.borderRadius.lg,
    backgroundColor: hexToRgba(c.primary, 0.08) as string,
    alignSelf: 'stretch',
  },
  selectedDatePillText: {
    flex: 1,
    fontSize: sf(14),
    fontWeight: '700',
    color: c.text,
    ...ANDROID_TEXT_COMPACT,
  },
  selectedDatePillAction: {
    fontSize: sf(12),
    fontWeight: '800',
    color: c.primary,
    ...ANDROID_TEXT_COMPACT,
  },
  slotGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignSelf: 'center',
    justifyContent: 'center',
    gap: BOOKING_SLOT_GRID_GAP,
    width: BOOKING_SLOT_WIDTH * 2 + BOOKING_SLOT_GRID_GAP,
    maxWidth: '100%',
  },
  slotGridCell: {
    width: BOOKING_SLOT_WIDTH,
    flexShrink: 0,
  },
  wizardFooterStack: {
    alignSelf: 'stretch',
    gap: appTheme.spacing.sm,
  },
  wizardBackBtn: {
    alignSelf: 'center',
    borderRadius: appTheme.borderRadius.lg,
  },
  wizardBackContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: appTheme.spacing.xs,
    paddingHorizontal: appTheme.spacing.md,
    borderRadius: appTheme.borderRadius.lg,
    minHeight: androidScaleSize(40),
    backgroundColor: hexToRgba(c.primary, 0.1) as string,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: hexToRgba(c.primary, 0.28) as string,
  },
  wizardBackText: {
    fontSize: sf(14),
    lineHeight: sf(18),
    fontWeight: '800',
    color: c.primary,
    ...ANDROID_TEXT_COMPACT,
  },
  datePickerTriggerWrap: {
    marginTop: appTheme.spacing.xs,
    marginBottom: 0,
  },
  androidPickerWrap: {
    marginTop: appTheme.spacing.sm,
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
    backgroundColor: slot_list_bg,
    alignSelf: 'stretch',
  },
  iosInlineCalendarWrap: {
    alignSelf: 'stretch',
    width: '100%',
    alignItems: 'center',
    overflow: 'hidden',
    ...studio_card,
    borderRadius: DASH_STUDIO_RADIUS,
    backgroundColor: slot_list_bg,
    paddingHorizontal: appTheme.spacing.xs,
    paddingVertical: appTheme.spacing.xs,
  },
  dateHint: {
    fontSize: sf(12),
    lineHeight: sf(16),
    color: c.textSecondary,
    ...ANDROID_TEXT_COMPACT,
  },
  slotsLoadingWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.sm,
  },
  slotsLoading: {
    fontSize: sf(14),
    color: c.textSecondary,
    ...ANDROID_TEXT_COMPACT,
  },
  slotsEmpty: {
    fontSize: sf(14),
    lineHeight: sf(20),
    color: c.textSecondary,
    paddingVertical: appTheme.spacing.sm,
    ...ANDROID_TEXT_COMPACT,
  },
  slotChip: {
    position: 'relative',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.sm,
    borderRadius: appTheme.borderRadius.xl,
    backgroundColor: slot_list_bg,
    minHeight: androidScaleSize(58),
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: hexToRgba(c.text, 0.1) as string,
    gap: 3,
  },
  slotChipDisabled: {
    opacity: 0.45,
    backgroundColor: hexToRgba(c.text, 0.04) as string,
  },
  slotChipActive: {
    backgroundColor: c.primaryUltra ?? hexToRgba(c.primary, 0.16),
    borderColor: c.primary,
    borderWidth: 1,
    shadowColor: c.primary,
    shadowOpacity: 0.18,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 },
    elevation: Platform.OS === 'android' ? 2 : 0,
  },
  slotChipText: {
    fontSize: sf(15),
    lineHeight: sf(20),
    color: c.text,
    fontWeight: '600',
    textAlign: 'center',
    width: '100%',
    ...ANDROID_TEXT_COMPACT,
  },
  slotChipTextActive: {
    color: c.primary,
    fontWeight: '700',
  },
  slotChipTextDisabled: {
    color: c.textLight,
    textDecorationLine: 'line-through',
  },
  slotChipBlockedHint: {
    fontSize: sf(9),
    fontWeight: '600',
    color: c.textLight,
    textAlign: 'center',
    ...ANDROID_TEXT_COMPACT,
  },
  slotChipCheck: {
    position: 'absolute',
    top: 7,
    right: 7,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: c.primary,
  },
  notesInputWrap: {
    marginBottom: 0,
  },
  modalFooter: {
    flexShrink: 0,
    paddingHorizontal: appTheme.spacing.lg,
    paddingTop: appTheme.spacing.md,
    paddingBottom: appTheme.spacing.xs,
    backgroundColor: c.surface,
    gap: appTheme.spacing.sm,
    ...createSheetFooterDividerStyle(c),
  },
  });
}
